import {
  Center,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  useDisclosure,
} from '@chakra-ui/react'
import { RoleType } from 'api-user-client'
import React, { useCallback, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'

import { SearchResults } from '~component/Search/SearchResults'
import { Input, Loader } from '~shared-components/components'
import { ErrorBoundary } from '~shared-components/components/ErrorBoundary/ErrorBoundary'
import {
  EXTERNAL_USER_ADMIN_PER_COUNTRY,
  INVOICES_PAGE_PER_COUNTRY,
  QUOTES_PAGE_PER_COUNTRY,
} from '~shared-components/components/FlagRender/frozenFlags'
import { useLoggedInUser } from '~shared-components/hooks/useLoggedInUser'
import {
  SEARCH_MIN_CHARACTERS,
  useSearch,
} from '~shared-components/hooks/useSearch'
import { useSearchQuery } from '~shared-components/hooks/useSearchQuery'
import { useTranslations } from '~shared-components/hooks/useTranslations'
import { ConfigSelector } from '~shared-components/redux/selectors/config.selector'
import { LocationSelector } from '~shared-components/redux/selectors/location.selector'

export const Search = () => {
  const t = useTranslations()

  const country = useSelector(ConfigSelector.localeCountry)
  const { user } = useLoggedInUser()
  const showInvoices =
    INVOICES_PAGE_PER_COUNTRY[country] &&
    user !== undefined &&
    user.isRoleAny(RoleType.UserManager, RoleType.UserAdmin, RoleType.Admin)

  const showQuotes =
    !!QUOTES_PAGE_PER_COUNTRY[country] && !user?.isRole(RoleType.SuperAdmin)

  const showUsers =
    !!EXTERNAL_USER_ADMIN_PER_COUNTRY[country] &&
    user?.isRoleAbove(RoleType.UserAdmin, true)

  const query = useSelector(LocationSelector.query)

  const { isOpen, onClose, onOpen } = useDisclosure()
  const setOpen = (cond: boolean) => (cond ? onOpen() : onClose())

  const { input, onSearchQueryChange, query: search } = useSearchQuery(
    SEARCH_MIN_CHARACTERS,
  )

  useEffect(() => {
    if (!query || query.searchQuery === input) return

    onSearchQueryChange(query.searchQuery ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const searchOptions = {
    invoices: showInvoices,
    orders: true,
    products: true,
    quotes: showQuotes,
    search,
    users: showUsers,
  }
  const { data, isError, isFetching, isPreviousData } = useSearch({
    ...searchOptions,
    page: 1,
    pageSize: 6,
  })

  const contentRef = useRef<HTMLElement>(null)
  const triggerRef = useRef<HTMLInputElement>(null)

  const handleBlur = useCallback(
    event => {
      const relatedTarget = event.relatedTarget
      const targetIsContent = contentRef.current?.contains(relatedTarget)
      const targetIsTrigger = triggerRef.current?.contains(relatedTarget)

      if (isOpen && !targetIsContent && !targetIsTrigger) {
        onClose()
      }
    },
    [isOpen, onClose],
  )

  const { pathname, type } = useSelector(LocationSelector.location)

  useEffect(() => {
    onClose()
  }, [pathname, type, onClose])

  return (
    <Popover
      matchWidth
      autoFocus={false}
      isOpen={isOpen}
      onClose={onClose}
      placement="bottom-start"
      returnFocusOnClose={false}
      trigger={'none' as 'click'}
    >
      <PopoverTrigger>
        <Input
          ref={triggerRef}
          showClearButton
          data-testid="search-input"
          onBlur={handleBlur}
          onFocus={() => setOpen(input.length >= SEARCH_MIN_CHARACTERS)}
          onValueChange={(query: string) => {
            onSearchQueryChange(query)
            setOpen(query.length >= SEARCH_MIN_CHARACTERS)
          }}
          placeholder={t('filter.placeholder.global')}
          type="search"
          value={input}
        />
      </PopoverTrigger>
      <PopoverContent
        ref={contentRef}
        bg="white"
        maxWidth={['100vw', '100vw', '1024px']}
        width="100%"
      >
        <PopoverBody px={0} py={4} zIndex="200">
          {isError ? (
            <ErrorBoundary />
          ) : (
            <SearchResults
              data={isPreviousData ? undefined : data}
              onClick={onClose}
              options={searchOptions}
            />
          )}
          <Center>{isFetching && <Loader />}</Center>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}
